import { ApiService } from '../api'
import { AxiosInstance } from 'axios'
import { Translations, TranslationsFlattened, TranslationsMessages, TranslationsRaw } from './types'
import { TranslationsPlatform } from './const'
import { MongooseFields } from '../../types'

/**
 * Service manager for handling translation operations.
 */
export class TranslationsServiceManager {
  static instance: TranslationsServiceManager

  apiService: ApiService | undefined
  masterApiService: ApiService | undefined

  /**
   * Registers the API services for translations.
   * @param {AxiosInstance} axiosInstance - The Axios instance for the API service.
   * @param {AxiosInstance} masterAxiosInstance - The Axios instance for the master API service.
   * @throws Will throw an error if `axiosInstance` or `masterAxiosInstance` is not provided.
   */
  public static registerService(axiosInstance: AxiosInstance, masterAxiosInstance: AxiosInstance) {
    if (!axiosInstance) throw new Error('axiosInstance is required')
    if (!masterAxiosInstance) throw new Error('masterAxiosInstance is required')
    const instance = TranslationsServiceManager.getInstance()
    instance.apiService = new ApiService(axiosInstance)
    instance.masterApiService = new ApiService(masterAxiosInstance)
  }

  /**
   * Gets the singleton instance of the TranslationsServiceManager.
   * @returns {TranslationsServiceManager} The singleton instance.
   */
  static getInstance = (): TranslationsServiceManager => {
    if (!TranslationsServiceManager.instance) {
      TranslationsServiceManager.instance = new TranslationsServiceManager()
    }
    return TranslationsServiceManager.instance
  }

  /**
   * Maps an array of translations to an object of translation messages.
   * @param {Translations[]} translations - The array of translations.
   * @returns {TranslationsMessages} The mapped translation messages.
   */
  public static mapTranslationsToMessages = (translations: Translations[]): TranslationsMessages => {
    return translations.reduce((acc, translation) => {
      return {
        ...acc,
        [translation.key]: translation.value,
      }
    }, {})
  }

  /**
   * Ensures the master API service is defined.
   * @private
   * @throws Will throw an error if `masterApiService` is not defined.
   */
  static controlMasterApiService = () => {
    const masterApiService = TranslationsServiceManager.getInstance().masterApiService
    if (!masterApiService) {
      throw new Error('masterApiService is not defined')
    }
    return masterApiService
  }

  /**
   * Ensures the API service is defined.
   * @private
   * @throws Will throw an error if `apiService` is not defined.
   */
  static controlApiService = () => {
    const axiosService = TranslationsServiceManager.getInstance().apiService
    if (!axiosService) {
      throw new Error('apiService is not defined')
    }
    return axiosService
  }

  /**
   * Retrieves translations filtered by language and platform.
   * @param {Translations['language']} [language] - The language of the translations.
   * @param {Translations['platform']} [platform] - The platform of the translations.
   */
  public static getTranslations = async (language?: Translations['language'], platform?: Translations['platform']) => {
    const masterApiService = this.controlMasterApiService()
    return masterApiService.getList<Translations>('translations', {
      language,
      platform,
    })
  }

  /**
   * Retrieves app translations for a specified language.
   * @param {Translations['language']} [language] - The language of the translations.
   */
  public static getAppTranslations = async (language?: Translations['language']) => {
    const masterApiService = this.controlMasterApiService()
    const translations = await masterApiService.getList<Translations>('translations', {
      language,
      platform: TranslationsPlatform.APP,
    })
    return this.mapTranslationsToMessages(translations)
  }

  /**
   * Retrieves web translations for a specified language.
   * @param {Translations['language']} [language] - The language of the translations.
   */
  public static getWebTranslations = async (language?: Translations['language']) => {
    const masterApiService = this.controlMasterApiService()
    const translations = await masterApiService.getList<Translations>('translations', {
      language,
      platform: TranslationsPlatform.WEB,
    })
    return this.mapTranslationsToMessages(translations)
  }

  /**
   * Adds a new translation.
   * @param {Omit<Translations, MongooseFields>} translation - The translation to add.
   */
  public static addTranslation = async (translation: Omit<Translations, MongooseFields>) => {
    const apiService = this.controlApiService()
    return apiService.createItem<Translations>('translations', translation)
  }

  public static flattenTranslations = (
    obj: TranslationsRaw,
    parentKey = '',
    result: TranslationsFlattened[] = []
  ): TranslationsFlattened[] => {
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const newKey = parentKey ? `${parentKey}.${key}` : key
        const value = obj[key]
        if (typeof value === 'object' && value !== null) {
          this.flattenTranslations(value, newKey, result)
        } else {
          result.push({ key: newKey, value })
        }
      }
    }
    return result
  }

  public static bulkUpsertTranslations = async (
    obj: TranslationsRaw,
    language: Translations['language'],
    platform: Translations['platform']
  ) => {
    const flattenedTranslations = this.flattenTranslations(obj)
    const apiService = this.controlApiService()

    return await apiService.axios.post('translations/bulk', {
      translations: flattenedTranslations.map((translation) => {
        return {
          key: translation.key,
          value: translation.value,
          language,
          platform,
        }
      }),
    })
  }
}

/**
 * Retrieves app translations for a specified language.
 * @function
 * @param {Translations['language']} [language] - The language of the translations.
 */
export const getAppTranslations = TranslationsServiceManager.getAppTranslations

/**
 * Retrieves web translations for a specified language.
 * @function
 * @param {Translations['language']} [language] - The language of the translations.
 */
export const getWebTranslations = TranslationsServiceManager.getWebTranslations

/**
 * Adds a new translation.
 * @function
 * @param {Omit<Translations, MongooseFields>} translation - The translation to add.
 */
export const addTranslation = TranslationsServiceManager.addTranslation

/**
 * Add bulk translations
 * Pass it.json or en.json file as an object. The function will flat it and create translations
 */
export const bulkUpsertTranslations = TranslationsServiceManager.bulkUpsertTranslations
